import { Link } from "@inertiajs/react";
import DOMPurify from "dompurify";
import parse from "html-react-parser";
import { memo } from "react";

import BB_Button from "@/Components/BB_Button";
import BB_ImageWrapper from "@/Components/BB_ImageWrapper";
import CartIcon from "@/Icons/cart.svg?react";
import LinkIcon from "@/Icons/link.svg?react";
import MagnifyingGlassIcon from "@/Icons/magnifyingGlass.svg?react";
import NewsIcon from "@/Icons/news.svg?react";
import XMarkIcon from "@/Icons/xmark.svg?react";
import BB_Product from "@/types/BB_Product";

export type BB_UpdateIcon = "in_stock" | "out_of_stock" | "link_created";

export type BB_Update = {
	id: string;
	type: BB_UpdateIcon;
	button_text?: string | null;
	button_link?: string | null;

	a_created_at: string;
	info: {
		headline: string;
		body: string;
	};

	product?: BB_Product;
};

export type BB_ProductUpdatesProps = {
	updates: BB_Update[];
};

const BB_ProductUpdates = (props: BB_ProductUpdatesProps) => {
	const getIcon = (type: BB_UpdateIcon) => {
		switch (type) {
			case "in_stock":
				return <CartIcon className="text-background-900 h-4 w-4" />;
			case "out_of_stock":
				return <XMarkIcon className="text-background-900 h-4 w-4" />;
			case "link_created":
				return <MagnifyingGlassIcon className="text-background-900 h-4 w-4" />;
			default:
				return <NewsIcon className="text-background-900 h-4 w-4" />;
		}
	};

	const getParsedBody = (body: string) => {
		if (typeof window !== "undefined") {
			const cleanHtml = DOMPurify.sanitize(body);
			return parse(cleanHtml);
		} else {
			return <div dangerouslySetInnerHTML={{ __html: body }}></div>;
		}
	};

	return (
		<div>
			<ol className="relative ms-3 flex flex-col gap-6 border-s border-gray-200 dark:border-gray-700">
				{props.updates.map((update, index) => (
					<li key={update.id} className="ms-6">
						<div>
							<span className="bg-primary-500 ring-background-100 dark:ring-dark-background-300 dark:bg-primary-500 absolute -start-3 ml-[-2px] flex h-7 w-7 items-center justify-center rounded-full ring-8">
								{getIcon(update.type)}
							</span>
							<h3 className="mb-1 flex items-center text-lg font-semibold text-gray-900 dark:text-white">
								<span className={"text-pretty"}>{update.info.headline}</span>
								{index === 0 && (
									<span className="bg-primary-500 dark:bg-dark-primary-400 ms-3 me-2 rounded-sm px-2.5 py-0.5 text-sm font-medium text-black">
										Neuste
									</span>
								)}
							</h3>
							<time className="mb-2 block text-sm leading-none font-normal text-gray-400 dark:text-gray-500">
								{update.a_created_at} Uhr
							</time>
							{update.product?.image && (
								<Link
									href={route("p", {
										b_id: update.product.b_id,
										slug: update.product.slug
									})}>
									<div className="item-image relative mb-2 flex h-32 max-h-32 flex-1 items-center justify-center rounded-md bg-white sm:max-w-72">
										<BB_ImageWrapper
											bb_image={update.product.image}
											suffix="?class=3"
											addPadding={update.product.image.trimmed === 1}
											className="pointer-events-none h-32 max-h-32 max-w-full object-contain select-none"
											alt="Produkt"
										/>
										<div className="dark:bg-dark-background-50/10 pointer-events-none absolute inset-0"></div>
									</div>
								</Link>
							)}
							<p className="text-base font-normal text-pretty hyphens-auto text-gray-500 dark:text-gray-400">
								{getParsedBody(update.info.body)}
							</p>
							{update.button_text &&
								update.button_link &&
								update.type !== "out_of_stock" && (
									<div className={"mt-3"}>
										<BB_Button
											type="secondary"
											title={update.button_text}
											onClick={update.button_link ?? "#"}
											buttonClassName={"sm:w-fit sm:px-8"}
											icon={LinkIcon}
											iconClassName="h-5 w-5 mr-2 pointer-events-none"
										/>
									</div>
								)}
						</div>
					</li>
				))}
			</ol>
		</div>
	);
};

export default memo(BB_ProductUpdates);
